// src/components/stories/AnimatedText.tsx
//
// Progressive text reveal for a single story text block.
// Modes: word_by_word | line_by_line | typewriter | fade.
// - Pause/resume via the `paused` prop (CSS animation-play-state preserves position).
// - Restarts cleanly when remounted (the viewer keys it by slide id).
// - Honors prefers-reduced-motion (falls back to a single fade).

import { useMemo } from 'react';
import { useReducedMotion } from 'framer-motion';
import { cn } from '@/lib/utils';
import type { TextAnimation } from '@/data/stories/types';

interface AnimatedTextProps {
  text: string;
  animation: TextAnimation;
  paused?: boolean;
  className?: string;
  /** Marks the text as RTL content (affects nothing structurally; dir is on parent). */
  lang?: 'en' | 'ar';
}

function splitUnits(text: string, mode: TextAnimation['mode']): string[] {
  switch (mode) {
    case 'word_by_word':
      return text.split(/(\s+)/).filter((s) => s.length > 0);
    case 'typewriter':
      return Array.from(text);
    case 'line_by_line':
      return text.split(/(?<=[.!?؟،\n])\s+/).filter(Boolean);
    case 'fade':
    default:
      return [text];
  }
}

export function AnimatedText({ text, animation, paused, className, lang }: AnimatedTextProps) {
  const reduce = useReducedMotion();
  const mode: TextAnimation['mode'] = reduce ? 'fade' : animation.mode;
  const units = useMemo(() => splitUnits(text, mode), [text, mode]);
  const playState = paused ? 'paused' : 'running';

  if (mode === 'fade') {
    return (
      <span
        className={cn('story-unit', className)}
        lang={lang}
        style={{
          animationDelay: `${animation.delay}ms`,
          animationTimingFunction: animation.easing,
          animationPlayState: playState,
        }}
      >
        {text}
      </span>
    );
  }

  const isLine = mode === 'line_by_line';
  const isTyped = mode === 'typewriter';

  return (
    <span className={className} lang={lang} aria-label={text}>
      {units.map((unit, i) => {
        // whitespace tokens (word mode) shouldn't be animated boxes
        if (/^\s+$/.test(unit)) return <span key={i}>{unit}</span>;
        return (
          <span
            key={i}
            aria-hidden
            className={cn('story-unit', isLine && 'story-line', isTyped && 'story-typed')}
            style={{
              animationDelay: `${animation.delay + i * animation.speed}ms`,
              animationTimingFunction: animation.easing,
              animationPlayState: playState,
            }}
          >
            {unit}
            {isTyped ? '' : ''}
          </span>
        );
      })}
    </span>
  );
}
